import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { Button, Input, DatePicker, Tooltip } from 'antd';
import styled from 'styled-components';
import DeviceTypeTreeSelector from 'containers/DeviceTypeTreeSelector';

const { RangePicker } = DatePicker;
const iso8601format = 'YYYY-MM-DDTHH:mm:ss';

const TIMEFRAME = {
	DAY: 'DAY',
	WEEK: 'WEEK',
	MONTH: 'MONTH',
	ALL_TIME: 'ALL_TIME',
	CUSTOM: 'CUSTOM',
};

const ToolbarContainer = styled.div`
	margin-bottom: 16px;
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

const Row = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 12px;
`;

const TimestampLabelGroup = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
`;

const TimeframeButtonGroup = styled.div`
	display: flex;
	gap: 4px;
`;

const TimeframeButton = styled(Button)`
	${(props) => props.active && `
		background-color: #595959;
		color: #fff;
		border-color: #595959;
	`}
`;

const getTimeframeRange = (timeframe) => {
	const to = moment();
	switch (timeframe) {
		case TIMEFRAME.DAY:      return [moment().subtract(1, 'days'), to];
		case TIMEFRAME.WEEK:     return [moment().subtract(1, 'weeks'), to];
		case TIMEFRAME.MONTH:    return [moment().subtract(1, 'months'), to];
		case TIMEFRAME.ALL_TIME: return [moment('2000-01-01').startOf('day'), to];
		default:                 return [null, null];
	}
};

const CommandsGridToolbar = (props) => {
	const { grid, setGridParams, resetGridData, deviceTypesTreeData, deviceTypesIdToLabelMap } = props;

	const [timeframe, setTimeframe] = useState(TIMEFRAME.DAY);
	const [dateRange, setDateRange] = useState(getTimeframeRange(TIMEFRAME.DAY));
	const [deviceSerialNumber, setDeviceSerialNumber] = useState('');
	const [deviceType, setDeviceType] = useState(undefined);
	const [entryKey, setEntryKey] = useState('');
	const [searchText, setSearchText] = useState('');

	const getDeviceTypeName = (id) => {
		if (!id || !deviceTypesIdToLabelMap) return id;
		const fullPath = deviceTypesIdToLabelMap[id];
		if (!fullPath) return id;
		return fullPath.split('/').pop();
	};

	const findDeviceTypeIdByDisplayName = (displayName) => {
		if (!displayName || !deviceTypesIdToLabelMap) return undefined;
		const entry = Object.entries(deviceTypesIdToLabelMap || {}).find(
			([, path]) => path && path.split('/').pop() === displayName
		);
		return entry ? entry[0] : undefined;
	};

	useEffect(() => {
		const filters = grid.params.filters || {};
		if (typeof filters === 'object' && Object.keys(filters).length > 0) {
			if (filters.deviceSerialNumber) setDeviceSerialNumber(filters.deviceSerialNumber);
			if (filters.entryKey) setEntryKey(filters.entryKey);
			if (filters.deviceType) setDeviceType(findDeviceTypeIdByDisplayName(filters.deviceType));
			if (filters.timestampAfter || filters.timestampBefore) {
				setTimeframe(TIMEFRAME.CUSTOM);
				setDateRange([
					filters.timestampAfter ? moment(filters.timestampAfter) : null,
					filters.timestampBefore ? moment(filters.timestampBefore) : null,
				]);
			}
			if (grid.params.search) setSearchText(grid.params.search);
		}
	}, []);

	const buildFilters = () => {
		const filters = {};
		const [from, to] = dateRange;
		if (from) filters.timestampAfter = from.format(iso8601format);
		if (to) filters.timestampBefore = to.format(iso8601format);
		if (deviceSerialNumber) filters.deviceSerialNumber = deviceSerialNumber;
		if (deviceType) filters.deviceType = getDeviceTypeName(deviceType);
		if (entryKey) filters.entryKey = entryKey;
		return filters;
	};

	const handleGetData = () => {
		setGridParams({
			...grid.params,
			filters: buildFilters(),
			search: searchText || '',
			pagination: {
				...grid.params.pagination,
				current: 1,
			},
		});
	};

	const handleTimeframeClick = (tf) => {
		setTimeframe(tf);
		const [from, to] = getTimeframeRange(tf);
		setDateRange([from, to]);
	};

	const handleCustomRangeChange = (range) => {
		setTimeframe(TIMEFRAME.CUSTOM);
		setDateRange(range || [null, null]);
	};

	const handleReset = () => {
		const defaultRange = getTimeframeRange(TIMEFRAME.DAY);
		setTimeframe(TIMEFRAME.DAY);
		setDateRange(defaultRange);
		setDeviceSerialNumber('');
		setDeviceType(undefined);
		setEntryKey('');
		setSearchText('');
		resetGridData();
	};

	const canQuery = !!(deviceType && deviceSerialNumber);

	return (
		<ToolbarContainer>
			<Row>
				<TimestampLabelGroup>
					<span style={{ color: '#595959', fontSize: 12, fontWeight: 500 }}>Timestamp:</span>
					<TimeframeButtonGroup>
						{[TIMEFRAME.DAY, TIMEFRAME.WEEK, TIMEFRAME.MONTH, TIMEFRAME.ALL_TIME, TIMEFRAME.CUSTOM].map((tf) => (
						<TimeframeButton
							key={tf}
							active={timeframe === tf ? 1 : 0}
							onClick={() => tf !== TIMEFRAME.CUSTOM ? handleTimeframeClick(tf) : setTimeframe(TIMEFRAME.CUSTOM)}
						>
						{tf === TIMEFRAME.ALL_TIME ? 'All time' : tf.charAt(0) + tf.slice(1).toLowerCase()}
					</TimeframeButton>
				))}
					</TimeframeButtonGroup>
				</TimestampLabelGroup>

				{timeframe === TIMEFRAME.CUSTOM && (
					<RangePicker
						showTime
						value={dateRange[0] && dateRange[1] ? [dateRange[0], dateRange[1]] : undefined}
						onChange={handleCustomRangeChange}
						placeholder={['From', 'To']}
					/>
				)}

				{timeframe !== TIMEFRAME.CUSTOM && (
					<span style={{ color: '#595959', fontSize: 12 }}>
						{dateRange[0]?.format('YYYY-MM-DD HH:mm')} → {dateRange[1]?.format('YYYY-MM-DD HH:mm')}
					</span>
				)}
			</Row>

			<Row>
				<DeviceTypeTreeSelector
					value={deviceType}
					onChange={setDeviceType}
					multiSelect={false}
					allowClear
					placeholder="Device Type"
					style={{ width: 280 }}
				/>
				<Input
					placeholder="Device Serial Number"
					value={deviceSerialNumber}
					onChange={(e) => setDeviceSerialNumber(e.target.value)}
					allowClear
					style={{ width: 220 }}
				/>
				<Input
					placeholder="Entry Key"
					value={entryKey}
					onChange={(e) => setEntryKey(e.target.value)}
					allowClear
					style={{ width: 200 }}
				/>
				<Input
					placeholder="Search"
					value={searchText}
					onChange={(e) => setSearchText(e.target.value)}
					allowClear
					style={{ width: 180 }}
				/>
				<Tooltip title={canQuery ? 'Fetch commands data' : 'Device Type and Serial Number are required'}>
					<Button
						type="primary"
						onClick={handleGetData}
						disabled={!canQuery}
					>
						Get Data
					</Button>
				</Tooltip>
				<Button onClick={handleReset}>Reset</Button>
				{/* Commands CSV export intentionally not offered here: the facade on the SB2 line
				    (staging/prod) has no COMMANDS export type (LUM-1688 ships with the SB3 train). */}
			</Row>
		</ToolbarContainer>
	);
};

export default CommandsGridToolbar;
